import MaterialTable, { Column } from 'material-table';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWindowHeight } from '@react-hook/window-size/throttled'
import { AuditTrail, ConversionType } from '../../gen/api/api';
import useHelpers from '../../helpers/datetime';
import useFeatures from '../../helpers/features';
import ApiService from '../../services/ApiService';
import { format } from 'date-fns';
import { makeStyles, Theme, createStyles, FormControl, Select, Icon } from '@material-ui/core';
import { TYPE_LABEL } from '../../shared/constants/GeneralDataFormConstants';
import { appStyle } from '../../AppStyle';
import { useGlobal } from 'reactn';

interface AuditTrailsTableState {
    columns: Array<Column<AuditTrail>>;
    data: AuditTrail[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
        },
        selectEmpty: {
            marginTop: theme.spacing(2),
        },
    }),
);

const { featureEnabledGrocerPointsConversion } = useFeatures();

const AuditTrailsList = () => {
    const { configurationId } = useParams();
    const [globalState, setGlobalState] = useGlobal();

    const classes = useStyles();
    const [typeFilterState, setTypeFilterState] = React.useState<{ type: ConversionType; name: string }>({
        type: ConversionType.CURRENCYCONVERSION,
        name: TYPE_LABEL.get(ConversionType.CURRENCYCONVERSION) || '',
    });

    const [state, setState] = useState<AuditTrailsTableState>({
        columns: [
            {
                title: 'User',
                field: 'changedBy',
                filtering: true,
                customSort: (a, b, type) => {
                    const [aValue, bValue] = type == 'row'
                        ? [a.changedBy, b.changedBy] : [a, b] as [string, string];
                    return aValue!.toLowerCase() < bValue!.toLowerCase() ? -1 : 1;
                },
            },
            {
                title: 'ID',
                field: 'rocId',
                defaultFilter: configurationId,
                customSort: (a, b, type) => {
                    const [aValue, bValue] = type == 'row'
                        ? [a.rocId, b.rocId] : [a, b] as [string, string];
                    return Number(aValue ? aValue.replace('ROC', '') : '0')
                        - Number(bValue ? bValue.replace('ROC', '') : '0');
                },
            },
            {
                title: 'Changed Field',
                field: 'attributeName',
                customSort: (a, b, type) => {
                    const [aValue, bValue] = type == 'row'
                        ? [a.attributeName, b.attributeName] : [a, b] as [string, string];
                    return aValue!.toLowerCase() < bValue!.toLowerCase() ? -1 : 1;
                },
            },
            { title: 'Value Before', field: 'valueBefore', sorting: false, filtering: false },
            { title: 'Value After', field: 'valueAfter', sorting: false, filtering: false },
            {
                title: 'Timestamp',
                field: 'changedAt',
                type: 'datetime',
                filtering: false,
                render: (rowOrGroup, type) => {
                    const changedAt = type == 'row' ? rowOrGroup.changedAt : rowOrGroup as Date;
                    return changedAt ? format(new Date(changedAt), 'MM/dd/yyyy hh:mm\xa0a') : '';
                },
            },
        ],
        data: [],
    });

    const [dataByType, setDataByType] = useState(
        new Map<ConversionType, AuditTrail[]>([
            [ConversionType.CURRENCYCONVERSION, []],
            [ConversionType.GROCERPOINTSCONVERSION, []],
            [ConversionType.FISCONVERSION, []],
        ]),
    );

    const { getTime } = useHelpers();

    const allowFiltering = () => {
        return featureEnabledGrocerPointsConversion() && configurationId === undefined;
    }

    const handleTypeFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const name = event.target.name as keyof typeof typeFilterState;
        setTypeFilterState({
            ...typeFilterState,
            [name]: event.target.value,
        });

        setState({
            columns: state.columns,
            data: dataByType.get(event.target.value as ConversionType) || [],
        });
    };

    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [dataLoadingError, setDataLoadingError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const auditTrails = await ApiService().fetchAuditTrails();

            const data = auditTrails.data.sort((a, b) => getTime(b.changedAt) - getTime(a.changedAt));

            setDataByType(
                dataByType.set(
                    ConversionType.CURRENCYCONVERSION,
                    data.filter((value) => value.type == ConversionType.CURRENCYCONVERSION),
                ),
            );
            setDataByType(
                dataByType.set(
                    ConversionType.GROCERPOINTSCONVERSION,
                    data.filter((value) => value.type == ConversionType.GROCERPOINTSCONVERSION),
                ),
            );
            setDataByType(
                dataByType.set(
                    ConversionType.FISCONVERSION,
                    data.filter((value) => value.type == ConversionType.FISCONVERSION),
                ),
            );

            const auditTrailData = allowFiltering() ? (dataByType.get(typeFilterState.type) || []) : data;

            setState({
                columns: state.columns,
                data: auditTrailData
            });

            setDataLoaded(true);
        };
        fetchData()
            .catch(error => setDataLoadingError(`${error}`));
    }, []);

    const conversionTypeDropdown = (<>{allowFiltering() && <>
        <FormControl className={classes.formControl}>
            <Select
                native
                value={typeFilterState.type}
                onChange={handleTypeFilterChange}
                inputProps={{
                    name: 'type',
                    id: 'type',
                }}
            >
                <option value={ConversionType.CURRENCYCONVERSION}>
                    {TYPE_LABEL.get(ConversionType.CURRENCYCONVERSION)}
                </option>
                <option value={ConversionType.GROCERPOINTSCONVERSION}>
                    {TYPE_LABEL.get(ConversionType.GROCERPOINTSCONVERSION)}
                </option>
                <option value={ConversionType.FISCONVERSION}>
                    {TYPE_LABEL.get(ConversionType.FISCONVERSION)}
                </option>
            </Select>
        </FormControl>
        <hr />
    </>}</>);

    const windowHeight = useWindowHeight();
    const canPinHeader = windowHeight >= appStyle.window.minHeightToUseStickyHeaderAndFooter;
    const pinHeader = globalState.pinHeader && canPinHeader;

    return (
        <div>
            <MaterialTable
                options={{
                    search: false,
                    paging: true,
                    filtering: allowFiltering(),
                    grouping: true,
                    pageSize: 20,
                    pageSizeOptions: [20, 50, 100, 1000],
                    emptyRowsWhenPaging: false,
                    padding: 'dense',
                    // Sticky header isn't supported yet, so we use hardcoded maxBodyHeight with position: 'sticky'
                    headerStyle: { position: 'sticky', ...appStyle.table.headerStyle },
                    maxBodyHeight: pinHeader ? 'calc(100vh - 276px)' : undefined,
                    exportButton: true,
                }}
                onRowClick={
                    // onRowClick is required to enable style on mouse hover
                    () => { }
                }
                title="Audit Log"
                columns={state.columns}
                data={state.data}
                actions={[
                    {
                        icon: () => conversionTypeDropdown,
                        tooltip: "Select Conversion Type",
                        isFreeAction: true,
                        onClick: () => { },
                    },
                    // Only display the button if the window height is large enough, and rotate icon while disabled
                    ...(canPinHeader ? [{
                        icon: pinHeader
                            ? "push_pin"
                            : () => <Icon style={{ transform: "rotate(45deg)" }}>push_pin</Icon>,
                        tooltip: 'Pin Header to Stay on Top',
                        isFreeAction: true,
                        onClick: () => { setGlobalState({ pinHeader: !pinHeader }) },
                    }] : []),
                ]}
                // Display the default message of "No records to display" only if the data was loaded successfully
                localization={dataLoaded ? undefined : {
                    body: {
                        emptyDataSourceMessage: dataLoadingError
                            ? `Loading the content failed - ${dataLoadingError}`
                            : "Loading the content...",
                    },
                }}
            >
            </MaterialTable>
        </div>
    );
};

export default AuditTrailsList;
